import { useEffect, useState } from "react";

interface ChannelAnalytics {
  channelId: string;
  channelTitle: string;
  views: number;
  estimatedMinutesWatched: number;
  averageViewDuration: number;
  subscribersGained: number;
  subscribersLost: number;
  likes: number;
  comments: number;
  shares: number;
  topVideos: Array<{ videoId: string; views: number }>;
}

interface AnalyticsResponse {
  success: boolean;
  startDate?: string;
  endDate?: string;
  channels?: ChannelAnalytics[];
  errors?: Array<{ channelId: string; channelTitle: string; error: string }>;
}

function compact(value: number): string {
  return new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 }).format(value || 0);
}

function duration(seconds: number): string {
  const value = Math.max(0, Math.round(seconds || 0));
  const minutes = Math.floor(value / 60);
  return `${minutes}:${String(value % 60).padStart(2, "0")}`;
}

function watchHours(minutes: number): string {
  return `${(Math.max(0, minutes || 0) / 60).toLocaleString("en-US", { maximumFractionDigits: 1 })}h`;
}

/** Native YouTube Studio metrics; distinct from SalesGrid lead and cash attribution. */
export function YouTubeStudioMetrics({ startDate, endDate }: { startDate: string; endDate: string }) {
  const [data, setData] = useState<AnalyticsResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    fetch(`/api/youtube/analytics?${new URLSearchParams({ startDate, endDate })}`, { signal: controller.signal })
      .then((response) => response.json())
      .then((json: AnalyticsResponse) => setData(json))
      .catch((error) => {
        if (error.name !== "AbortError") setData({ success: false });
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [startDate, endDate]);

  if (loading) return <div className="h-40 rounded-xl border border-border bg-card animate-pulse" />;
  if (!data?.success || (!data.channels?.length && !data.errors?.length)) return null;

  return (
    <section className="space-y-3 rounded-xl border border-border bg-card p-4 md:p-5">
      <div>
        <h2 className="text-base font-semibold">YouTube Studio Analytics</h2>
        <p className="text-xs text-muted-foreground">
          Native YouTube reporting for {data.startDate} to {data.endDate}. These are not SalesGrid attribution metrics.
        </p>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        {(data.channels || []).map((channel) => {
          const netSubscribers = channel.subscribersGained - channel.subscribersLost;
          return (
            <div key={channel.channelId} className="rounded-lg border border-border bg-muted/20 p-4">
              <div className="mb-3 flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-semibold">{channel.channelTitle || channel.channelId}</h3>
                  <p className="text-[11px] text-muted-foreground">Connected channel</p>
                </div>
                <span className="text-xs text-muted-foreground">{compact(channel.likes)} likes</span>
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm sm:grid-cols-4">
                <div><p className="text-[11px] text-muted-foreground">Views</p><p className="font-semibold">{compact(channel.views)}</p></div>
                <div><p className="text-[11px] text-muted-foreground">Watch time</p><p className="font-semibold">{watchHours(channel.estimatedMinutesWatched)}</p></div>
                <div><p className="text-[11px] text-muted-foreground">Avg. view</p><p className="font-semibold">{duration(channel.averageViewDuration)}</p></div>
                <div><p className="text-[11px] text-muted-foreground">Net subscribers</p><p className="font-semibold">{netSubscribers >= 0 ? "+" : ""}{compact(netSubscribers)}</p></div>
              </div>
              <p className="mt-3 text-[11px] text-muted-foreground">{compact(channel.comments)} comments · {compact(channel.shares)} shares</p>
            </div>
          );
        })}
      </div>
      {(data.errors || []).map((error) => <p key={error.channelId} className="text-xs text-amber-700 dark:text-amber-300">{error.channelTitle || error.channelId}: YouTube Analytics is temporarily unavailable.</p>)}
    </section>
  );
}
